import React, { useEffect, useState } from "react";
import { Box, TextField, Button, CircularProgress, Snackbar, Alert, Typography } from "@mui/material";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const CustomersEdit = ({ customer = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const [firstName, setFirstName] = useState(customer?.first_name ?? "");
  const [lastName, setLastName] = useState(customer?.last_name ?? "");
  const [email, setEmail] = useState(customer?.email ?? "");
  const [phone, setPhone] = useState(customer?.phone ?? "");
  const [address, setAddress] = useState(customer?.address ?? "");
  const [zip, setZip] = useState(customer?.zip_code ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    setFirstName(customer?.first_name ?? "");
    setLastName(customer?.last_name ?? "");
    setEmail(customer?.email ?? "");
    setPhone(customer?.phone ?? "");
    setAddress(customer?.address ?? "");
    setZip(customer?.zip_code ?? "");
    setPassword("");
  }, [customer]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const validate = () => {
    if (!customer) {
      setSnack({ open: true, message: "No customer selected", severity: "error" });
      return false;
    }
    if (!firstName.trim()) {
      setSnack({ open: true, message: "First name is required", severity: "error" });
      return false;
    }
    if (!email.trim()) {
      setSnack({ open: true, message: "Email is required", severity: "error" });
      return false;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      setSnack({ open: true, message: "Enter valid email", severity: "error" });
      return false;
    }
    if (!phone.trim() || !/^\d{7,15}$/.test(phone.replace(/\D/g, ""))) {
      setSnack({ open: true, message: "Enter valid phone (7-15 digits)", severity: "error" });
      return false;
    }
    // password required for edit
    if (!password || password.length < 6) {
      setSnack({ open: true, message: "Password required (min 6 chars) to update", severity: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        id: customer.id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        zip_code: zip.trim(),
        password,
      };
      const res = await ApiServices.put(`customers/${customer.id}/`, payload);
      const data = res?.data ?? res;
      setSnack({ open: true, message: "Customer updated successfully", severity: "success" });
      onSuccess("Customer updated successfully", data);
    } catch (err) {
      console.error(err);
      let msg = "Failed to update customer.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = Object.values(d).flat().join(" ") || msg;
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <Typography sx={{ color: "#094c9f", fontWeight: 700 }}>Edit Customer</Typography>

      <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required fullWidth />
      <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth />
      <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
      <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} required fullWidth />
      <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} multiline rows={2} fullWidth />
      <TextField label="Zip code" value={zip} onChange={(e) => setZip(e.target.value)} fullWidth />
      <TextField
        label="Password (required)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        type="password"
        fullWidth
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#094c9f" }}>
          {loading ? <CircularProgress size={20} /> : "Save Changes"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersEdit;
